import { addYears, format, isWithinInterval, parseISO } from "date-fns";
import type { SecureVolumeProduct } from "../../store/types/secureVolume.types";

const formatDate = (iso: string) => {
  try {
    return format(parseISO(iso), "yyyy-MM-dd");
  } catch {
    return iso;
  }
};

/** Ex: "16 mån · 2026-07-01 – 2027-11-01 · handlar för 2028" */
export const formatProductPeriod = (p: SecureVolumeProduct) =>
  `${p.managementLengthMonths} mån · ${formatDate(p.managementPeriodStart)} – ${formatDate(
    p.managementPeriodEnd,
  )} · handlar för ${p.defaultTargetYear}`;

export const findProduct = (
  products: SecureVolumeProduct[] | undefined,
  productNo: number | undefined | null,
) => products?.find((p) => p.productNo === productNo);

/** Produktnamn, eller ett fallback-namn om produkten inte finns i listan */
export const getProductName = (
  products: SecureVolumeProduct[] | undefined,
  productNo: number | undefined | null,
) => findProduct(products, productNo)?.productName ?? (productNo != null ? `Produkt ${productNo}` : undefined);

/**
 * Förvaltningsperioden för ett ärende med givet målår.
 * Backend levererar perioden för produktens aktuella målår (defaultTargetYear); för
 * ett annat målår förskjuts backends datum med skillnaden i år. Ingen egen regel räknas ut.
 */
export const getManagementPeriodForTargetYear = (
  product: SecureVolumeProduct,
  targetYear: number,
): { start: Date; end: Date } | null => {
  try {
    const delta = targetYear - product.defaultTargetYear;
    return {
      start: addYears(parseISO(product.managementPeriodStart), delta),
      end: addYears(parseISO(product.managementPeriodEnd), delta),
    };
  } catch {
    return null;
  }
};

/** True när förvaltningsperioden för ärendets målår pågår just nu – volymerna ska då inte röras */
export const isManagementPeriodActive = (
  product: SecureVolumeProduct | undefined,
  targetYear: number | undefined,
  now: Date = new Date(),
): boolean => {
  if (!product || targetYear == null) return false;
  const period = getManagementPeriodForTargetYear(product, targetYear);
  if (!period) return false;
  return isWithinInterval(now, period);
};

export const formatPeriod = (period: { start: Date; end: Date }) =>
  `${format(period.start, "yyyy-MM-dd")} – ${format(period.end, "yyyy-MM-dd")}`;
